import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AdminSheet } from "@/components/admin/AdminSheet";
import { stageLevels, getStageForGender } from "@/lib/avatarData";
import { METRICS, formatValue } from "@/lib/metrics/types";
import { formatGateCurrent, type GateStatus } from "@/hooks/useGateStatus";
import { toast } from "sonner";
import { Check, Circle, Mail, VolumeX, RotateCcw, Award } from "lucide-react";
import { useCoaches } from "@/hooks/useCoaches";
import { RoleBadges } from "@/components/RoleBadges";
import { useIncomeProfile } from "@/hooks/useIncomeProfile";
import { FullRecord } from "@/components/coach/FullRecord";
import { CoachActions, CoachMemberExtras } from "@/components/coach/CoachMemberExtras";
import { MemberAvatarRepair } from "@/components/admin/MemberAvatarRepair";

const db = supabase as any;
const DAY = 864e5;

export interface Row {
  user_id: string;
  name: string;
  email: string | null;
  gender: string | null;
  stage: number;
  lastLog: string | null;
  chapters: number;
}

const stageName = (n: number, g: string | null) =>
  getStageForGender(stageLevels[Math.min(8, Math.max(1, n)) - 1], g === "female" ? "female" : "male").name;

const ago = (d: string | null) => {
  if (!d) return "never";
  const days = Math.floor((Date.now() - new Date(d).getTime()) / DAY);
  return days === 0 ? "today" : `${days}d ago`;
};

export function MemberReview({ coachMode = false }: { coachMode?: boolean } = {}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<Row | null>(null);
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin-member-review"],
    queryFn: async (): Promise<Row[]> => {
      const [{ data: profs }, { data: stages }, { data: logs }, book] = await Promise.all([
        db.from("profiles").select("user_id, full_name, email, gender").order("created_at", { ascending: false }).limit(1000),
        db.from("stage_advancement").select("user_id, current_stage"),
        db.from("metric_entries").select("user_id, recorded_at").order("recorded_at", { ascending: false }).limit(5000),
        db.from("book_responses").select("user_id"),
      ]);
      const st = Object.fromEntries((stages ?? []).map((s: any) => [s.user_id, s.current_stage]));
      const last: Record<string, string> = {};
      (logs ?? []).forEach((l: any) => { if (!last[l.user_id]) last[l.user_id] = l.recorded_at; });
      const ch: Record<string, number> = {};
      (book.error ? [] : book.data ?? []).forEach((b: any) => { ch[b.user_id] = (ch[b.user_id] ?? 0) + 1; });
      return (profs ?? []).map((p: any) => ({
        user_id: p.user_id,
        name: p.full_name || "Student",
        email: p.email,
        gender: p.gender,
        stage: st[p.user_id] ?? 1,
        lastLog: last[p.user_id] ?? null,
        chapters: ch[p.user_id] ?? 0,
      }));
    },
  });

  const list = useMemo(() => {
    const s = q.toLowerCase();
    return rows.filter((r) => !s || r.name.toLowerCase().includes(s) || (r.email ?? "").toLowerCase().includes(s));
  }, [rows, q]);

  const offTrack = (r: Row) => !r.lastLog || Date.now() - new Date(r.lastLog).getTime() > 14 * DAY;

  return (
    <div className="space-y-4">
      <Input placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm bg-card" />
      <div className="overflow-x-auto rounded-xl border border-amber-500/30 bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-amber-500/20 text-left text-[10px] uppercase tracking-[0.18em] text-amber-800">
            <tr>
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Stage</th>
              <th className="px-4 py-3">Last log</th>
              <th className="px-4 py-3">Chapters</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="px-4 py-6 text-muted-foreground">Loading…</td></tr>}
            {list.map((r) => (
              <tr key={r.user_id} onClick={() => setOpen(r)} className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-amber-500/5">
                <td className="px-4 py-3">
                  <div className="font-medium text-foreground">{r.name} <RoleBadges userId={r.user_id} /></div>
                  <div className="text-xs text-muted-foreground">{r.email}</div>
                </td>
                <td className="px-4 py-3 text-foreground/80">{r.stage} · {stageName(r.stage, r.gender)}</td>
                <td className="px-4 py-3 text-foreground/80">{ago(r.lastLog)}</td>
                <td className="px-4 py-3 text-foreground/80">{r.chapters}</td>
                <td className="px-4 py-3">
                  {offTrack(r) ? (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Off Track</span>
                  ) : (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">On track</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AdminSheet open={!!open} onOpenChange={(v) => !v && setOpen(null)} title={open?.name ?? ""} description={open?.email ?? undefined} size="md">
        {open && <MemberPanel row={open} coachMode={coachMode} />}
      </AdminSheet>
    </div>
  );
}

export function MemberPanel({ row, coachMode }: { row: Row; coachMode: boolean }) {
  const qc = useQueryClient();
  const { data: coaches } = useCoaches();
  const isCoach = !!coaches?.has(row.user_id);
  const { profile: income } = useIncomeProfile(row.user_id);
  const coach = useMutation({
    mutationFn: async () => {
      const { error } = await db.rpc("set_member_coach", { _user: row.user_id, _on: !isCoach });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(isCoach ? "Coach role removed" : "Promoted to coach");
      qc.invalidateQueries({ queryKey: ["coach-ids"] });
    },
    onError: (e: any) => toast.error("Couldn't change coach role", { description: e.message }),
  });
  const { data } = useQuery({
    queryKey: ["admin-member-panel", row.user_id],
    queryFn: async () => {
      const [gate, avatars, entries, pending, book] = await Promise.all([
        db.rpc("evaluate_member_gate", { _user: row.user_id }),
        db.from("user_avatars").select("stage_number, image_url, gender").eq("user_id", row.user_id),
        db.from("metric_entries").select("metric, value, recorded_at, created_at, details").eq("user_id", row.user_id).order("recorded_at", { ascending: true }),
        db.from("member_gate_status").select("id, from_stage, to_stage, advanced_at").eq("user_id", row.user_id).eq("review_status", "pending"),
        db.from("book_responses").select("chapter, answers, ai_score, ai_feedback, created_at").eq("user_id", row.user_id).order("chapter"),
      ]);
      return {
        gate: (gate.data ?? null) as GateStatus | null,
        avatars: avatars.data ?? [],
        entries: entries.data ?? [],
        pending: pending.data ?? [],
        book: book.error ? null : book.data ?? [],
      };
    },
  });

  const review = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.rpc("review_gate_crossing", { _status_id: id, _decision: "approved", _note: null });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Stage move approved");
      qc.invalidateQueries({ queryKey: ["admin-member-panel", row.user_id] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
    onError: (e: any) => toast.error("Couldn't approve", { description: e.message }),
  });

  const mute = useMutation({
    mutationFn: async () => {
      const { error } = await db.rpc("mute_member", { _target_user_id: row.user_id, _minutes: 1440, _reason: "Muted from member review" });
      if (error) throw error;
    },
    onSuccess: () => toast.success("Muted in Community for 24 hours"),
    onError: (e: any) => toast.error("Couldn't mute", { description: e.message }),
  });

  const resetAvatar = useMutation({
    mutationFn: async () => {
      const { error } = await db.rpc("admin_reset_member_avatar", { _user: row.user_id });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Photo and avatar cleared — they can upload a new photo now");
      qc.invalidateQueries({ queryKey: ["admin-member-panel", row.user_id] });
    },
    onError: (e: any) => toast.error("Couldn't reset", { description: e.message }),
  });

  const stage = data?.gate?.current_stage ?? row.stage;
  const avatar = data?.avatars.find((a: any) => a.stage_number === stage)?.image_url;

  return (
    <div className="space-y-3 p-4 sm:p-6">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full border-2 border-gold bg-muted">
          {avatar && <img src={avatar} alt="" className="h-full w-full object-cover object-top" />}
        </div>
        <div>
          <div className="font-display text-xl text-foreground">{row.name}</div>
          <div className="text-sm text-muted-foreground">Stage {stage} of 8 · {stageName(stage, row.gender)}</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            {(() => { const age = row.lastLog ? Date.now() - new Date(row.lastLog).getTime() : Infinity;
              const [dot, word] = age < 3 * 864e5 ? ["bg-emerald-600", "Active"] : age < 7 * 864e5 ? ["bg-gold", "Slipping"] : ["bg-destructive", "Inactive"];
              return <><span className={`h-2 w-2 rounded-full ${dot}`} />{word} · Last seen {ago(row.lastLog)}</>; })()}
          </div>
        </div>
      </div>

      <CoachActions memberId={row.user_id} />
      <MemberAvatarRepair key={row.user_id} memberId={row.user_id} name={row.name} />


      <CoachMemberExtras memberId={row.user_id} />

      <FullRecord memberId={row.user_id} entries={data?.entries ?? []} gate={data?.gate ?? null} stage={stage} book={data?.book ?? null} income={income} />
      {(data?.pending ?? []).map((p: any) => (
        <div key={p.id} className="flex items-center justify-between rounded-lg border border-amber-500 bg-amber-500/10 p-3 text-sm">
          <span>Moved Stage {p.from_stage} → {p.to_stage} · waiting for review</span>
          {!coachMode && <Button size="sm" onClick={() => review.mutate(p.id)} disabled={review.isPending} className="bg-amber-500 text-amber-950 hover:bg-amber-400">Approve</Button>}
        </div>
      ))}

      <section className="rounded-xl border border-amber-500/25 bg-muted/40 p-3">
        <p className="mb-2 font-display text-base">{coachMode ? "Tools" : "Admin tools"}</p>
        <div className="grid grid-cols-2 gap-2 [&_a]:w-full [&_button]:w-full">
        {row.email && (
          <Button variant="outline" size="sm" asChild>
            <a href={`mailto:${row.email}`}><Mail className="mr-1.5 h-4 w-4" />Email</a>
          </Button>
        )}
        {!coachMode && <>
        <Button variant="outline" size="sm" onClick={() => mute.mutate()} disabled={mute.isPending}>
          <VolumeX className="mr-1.5 h-4 w-4" />Mute 24h
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={resetAvatar.isPending}
          onClick={() => {
            if (window.confirm("Delete this member's photo and avatar? They'll be able to upload a new photo and make their avatar again.")) resetAvatar.mutate();
          }}
        >
          <RotateCcw className="mr-1.5 h-4 w-4" />Reset photo & avatar
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={coach.isPending || !coaches}
          onClick={() => {
            if (window.confirm(isCoach ? "Remove this member's coach role?" : "Promote this member to coach? They'll be able to post to Today's Musts, grade homework and see member progress.")) coach.mutate();
          }}
        >
          <Award className="mr-1.5 h-4 w-4" />{isCoach ? "Remove coach" : "Promote to coach"}
        </Button>
        </>}
        </div>
      </section>
      <p className="pt-2 text-center font-serif text-[11px] italic uppercase tracking-[0.25em] text-muted-foreground">Small steps. Bigger lives.</p>
    </div>
  );
}
